import type { ClientRequestStatus, ClientRequestType } from '@/types/client-requests'
import type { AtsPlacementStatus } from '@/types/ats/ats-placements'
import type { TranslationKey } from '@/translations/index'

const BLUE   = { bg: "#DBEAFE", text: "#1D4ED8" }
const AMBER  = { bg: "#FEF3C7", text: "#92400E" }
const PURPLE = { bg: "#EDE9FE", text: "#5B21B6" }
const LIME   = { bg: "#D9F99D", text: "#365314" }
const GREEN  = { bg: "#D1FAE5", text: "#065F46" }
const RED    = { bg: "#FEE2E2", text: "#991B1B" }
const GRAY      = { bg: "#F3F4F6", text: "#374151" }
const GRAY_MUTED = { bg: "#F3F4F6", text: "#6B7280" }

export const STATUS_BADGE_STYLES: Record<ClientRequestStatus, { bg: string; text: string }> = {
  Draft: GRAY_MUTED,
  RequestInitiated: BLUE,
  VerifyingWorker: AMBER,
  ReviewWorkerInfo: PURPLE,
  WorkerInfoReviewed: LIME,
  RequestAccepted: GREEN,
  Canceled: RED,
  // VMS-only Rejected status. Distinguished visually from Canceled (still
  // red but more muted) so the grid eyeballs as "rejection happened in
  // pre-verification" vs "whole request killed." Treat Rejected as a
  // terminal-for-this-attempt but not terminal-for-the-row state.
  Rejected: { bg: "#FED7AA", text: "#9A3412" },
  OnHold: GRAY,
  // OB Portal onboarding "in progress" (Request-source rows in the related-requests panel).
  InProgress: BLUE,
}

export const ATS_STATUS_BADGE_STYLES: Record<AtsPlacementStatus, { bg: string; text: string }> = {
  New: BLUE,
  PreVerificationRequested: AMBER,
  PreVerificationReview: PURPLE,
  PreVerificationComplete: LIME,
  RequestAccepted: GREEN,
  Canceled: RED,
  OnHold: GRAY,
  // Pre-alignment ATS spellings — styled identically so rows still holding a legacy value render a
  // correct badge in the window between this deploy and the Phase 7 status migration.
  Accepted: GREEN,
  Cancelled: RED,
}

// ─── Status / request-type → i18n key maps ───────────────────────────────────
// Shared presentation maps for the client-request (incl. VMS + OB-Portal Request) family.
// Consumed by the unified hub's related-requests panel and the readonly detail view.

export const STATUS_TRANSLATION_KEY: Record<ClientRequestStatus, TranslationKey> = {
  Draft: "statusDraft",
  RequestInitiated: "statusInitiated",
  VerifyingWorker: "statusVerifyingWorker",
  ReviewWorkerInfo: "statusReviewWorkerInfo",
  WorkerInfoReviewed: "statusWorkerInfoReviewed",
  RequestAccepted: "statusAccepted",
  Canceled: "statusCancelled",
  Rejected: "statusRejected",
  OnHold: "statusOnHold",
  InProgress: "statusInProgress",
}

export const REQUEST_TYPE_TRANSLATION_KEY: Record<ClientRequestType, TranslationKey> = {
  Onboarding: "requestTypeOnboarding",
  General: "requestTypeGeneral",
  Extension: "requestTypeExtension",
  Redeployment: "requestTypeRedeployment",
  Termination: "requestTypeTermination",
  Other: "requestTypeOther",
}
